import * as React from 'react';
import { Play, ExternalLink } from 'lucide-react';

type MediaPreviewProps = {
  provider: 'YouTube' | 'Instagram' | 'TikTok' | 'Facebook' | string;
  url: string;
  // Optional: if you already have these from your pipeline, pass them
  thumbnailUrl?: string | null;   // remote image URL
  title?: string | null;
};

export const MediaPreview: React.FC<MediaPreviewProps> = ({ provider, url, thumbnailUrl, title }) => {
  const isYouTube = /youtu\.be|youtube\.com/.test(url);

  // Use your existing image proxy to avoid 403s
  const proxied = thumbnailUrl ? (() => {
    try {
      // If it's already our proxy endpoint, use it as-is
      if (thumbnailUrl.startsWith('/img?')) {
        return thumbnailUrl;
      }
      
      // Fix old PhotoService URLs by extracting photo_reference
      if (thumbnailUrl.includes('/PhotoService.GetPhoto')) {
        // Convert old PhotoService URLs to use proper proxy
        const url = new URL(thumbnailUrl);
        const photoRef = url.searchParams.get('photoreference');
        if (photoRef) {
          return `/img?photo_ref=${encodeURIComponent(photoRef)}&w=1200`;
        }
        // If no photo_reference, this URL is broken - return null to show fallback
        return null;
      }
      
  // Use regular proxy for other URLs and pass ref where possible
  return `/img?u=${encodeURIComponent(thumbnailUrl)}&w=1200&ref=${encodeURIComponent(url)}`;
    } catch {
  return `/img?u=${encodeURIComponent(thumbnailUrl)}&w=1200&ref=${encodeURIComponent(url)}`;
    }
  })() : null;

  if (isYouTube) {
    const id = extractYouTubeId(url);
    if (id) {
      const src = `https://www.youtube.com/embed/${id}?rel=0`;
      return (
        <div className="relative aspect-video w-full overflow-hidden rounded-t-2xl bg-black">
          <iframe
            src={src}
            title={title || 'Video preview'}
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
            allowFullScreen
            className="h-full w-full"
          />
        </div>
      );
    }
  }

  // Fallback for IG/TikTok/Facebook or unknown providers
  return (
    <div className="relative aspect-video w-full overflow-hidden rounded-t-2xl bg-neutral-900">
      {proxied ? (
        <>
          <img 
            src={proxied} 
            alt={title || 'Preview image'} 
            className="h-full w-full object-cover opacity-90"
            loading="lazy"
            referrerPolicy="no-referrer"
          />
          <button
            onClick={() => window.open(url, '_blank', 'noopener,noreferrer')}
            className="absolute inset-0 grid place-items-center focus:outline-none group"
            aria-label={`Abrir publicación en ${provider}`}
          >
            <div className="flex items-center gap-2 rounded-full bg-white/90 backdrop-blur-sm px-4 py-2 text-sm font-medium shadow-lg group-hover:bg-white group-focus:bg-white transition-colors">
              <Play className="w-4 h-4" />
              <span>Ver publicación</span>
            </div>
          </button>
        </>
      ) : (
        <div className="absolute inset-0 grid place-items-center text-white/80">
          <div className="text-center">
            <div className="mb-4 text-4xl opacity-60">📱</div>
            <p className="mb-4 text-sm text-white/70">Vista previa no disponible</p>
            <a
              href={url}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 rounded-lg bg-white px-4 py-2 text-sm font-medium text-black hover:bg-gray-100 transition-colors"
            >
              <ExternalLink className="w-4 h-4" />
              Abrir publicación
            </a>
          </div>
        </div>
      )}
    </div>
  );
};

function extractYouTubeId(u: string) {
  try {
    const url = new URL(u);
    if (url.hostname === 'youtu.be') return url.pathname.slice(1);
    if (url.hostname.includes('youtube.com')) return url.searchParams.get('v');
  } catch {}
  return null;
}

export default MediaPreview;
